import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Inbox, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../../services/api';

const PendingUpgradesCard = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await adminAPI.listUpgradeRequests('pending');
      setRows(data.requests || []);
    } catch {
      toast.error('Could not load upgrade requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, action) => {
    setActioning(id);
    try {
      if (action === 'approve') {
        await adminAPI.approveUpgradeRequest(id);
        toast.success('Approved — user upgraded to Pro');
      } else {
        await adminAPI.rejectUpgradeRequest(id);
        toast.success('Request rejected');
      }
      setRows((r) => r.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActioning(null);
    }
  };

  return (
    <Card className="bg-gray-800 border-gray-700" data-testid="admin-pending-upgrades-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Inbox className="w-5 h-5 text-purple-400" />
          Pending Upgrades
          {rows.length > 0 && (
            <Badge className="bg-purple-600 text-white">{rows.length}</Badge>
          )}
        </CardTitle>
        <CardDescription className="text-gray-400">
          Income Blueprint purchases submitted by members — approve to Pro in one click
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="admin-upgrades-empty">
            No pending requests. 🎉
          </p>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <div
                key={r.id}
                className="p-4 bg-gray-700/50 rounded-lg border border-gray-600 flex flex-wrap items-center justify-between gap-3"
                data-testid={`upgrade-request-${r.id}`}
              >
                <div className="min-w-0">
                  <p className="font-medium text-white truncate">{r.user_email}</p>
                  <p className="text-xs text-gray-400">
                    Order email: <span className="text-gray-300">{r.order_email}</span>
                    {' · '}
                    {new Date(r.created_at).toLocaleString()}
                  </p>
                  {r.note && <p className="text-xs text-gray-500 mt-1 italic">“{r.note}”</p>}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => act(r.id, 'approve')}
                    disabled={actioning === r.id}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid={`upgrade-approve-${r.id}`}
                  >
                    <Check size={14} className="mr-1" /> Approve → Pro
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => act(r.id, 'reject')}
                    disabled={actioning === r.id}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    data-testid={`upgrade-reject-${r.id}`}
                  >
                    <X size={14} className="mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PendingUpgradesCard;
